import {
  deleteSubscription,
  findDeviceByEndpoint,
  isRegistrationConflict,
  registerSubscription,
} from "./push-service.js";
import {
  currentEndpoint,
  subscribeThisDevice,
  unsubscribeThisDevice,
} from "./subscription.js";

/**
 * Thrown when this browser's push registration belongs to another account and a
 * fresh one could not be obtained.
 */
export class DeviceClaimedError extends Error {
  /** Builds the error with its fixed message. */
  constructor() {
    super("This device is registered to another account");
    this.name = "DeviceClaimedError";
  }
}

/**
 * Registers a freshly minted subscription, retrying once with a new one when the
 * endpoint this browser holds belongs to a different account.
 *
 * A browser hands the same endpoint to whoever is signed in on it, so signing in
 * after someone else on a shared device inherits their registration. WSC will not
 * move a row between accounts, because a genuine handover and a replayed endpoint
 * reach it as the same request. Discarding the subscription and asking for a new
 * one is the one thing only this browser can do, which is what makes it proof.
 * @param {string} vapidPublicKey The server's VAPID public key
 * @returns {Promise<object>} The registered subscription
 * @throws {DeviceClaimedError} When a fresh subscription is claimed as well
 */
async function register(vapidPublicKey) {
  try {
    return await registerSubscription(
      await subscribeThisDevice(vapidPublicKey),
    );
  } catch (error) {
    if (!isRegistrationConflict(error)) throw error;
  }

  await unsubscribeThisDevice();

  try {
    return await registerSubscription(
      await subscribeThisDevice(vapidPublicKey),
    );
  } catch (error) {
    // The push service handed back an endpoint someone else still holds. Their
    // row is now undeliverable, so the next digest tick retires it and a later
    // attempt succeeds.
    if (isRegistrationConflict(error)) throw new DeviceClaimedError();
    throw error;
  }
}

/**
 * Forgets the row a replaced subscription left behind.
 *
 * The endpoint identifies the device, so a re-mint leaves the old row owned by
 * this member, still enabled, and no longer recognisable as this device: the
 * settings page lists it as another device and every digest tick sends to an
 * endpoint that can only refuse. Failing to clean it up leaves that mess rather
 * than costing the registration that just succeeded, so it is logged and swallowed.
 * @param {string} endpoint The endpoint the replaced subscription held
 * @returns {Promise<void>} Resolves once the row is gone or the attempt failed
 */
async function forgetReplaced(endpoint) {
  try {
    const device = await findDeviceByEndpoint(endpoint);

    if (device) await deleteSubscription(device.id);
  } catch (error) {
    console.error("Failed to remove the replaced push subscription", error);
  }
}

/**
 * Registers this device for push, clearing away the registration it replaces.
 *
 * A browser hands back its existing subscription for any key it is asked for, so
 * enabling is also the moment a device minted under a rotated VAPID pair gets a
 * usable one.
 * @param {string} vapidPublicKey The server's VAPID public key
 * @returns {Promise<object>} The registered subscription
 * @throws {DeviceClaimedError} When a fresh subscription is claimed as well
 */
export async function enableThisDevice(vapidPublicKey) {
  const previousEndpoint = await currentEndpoint();

  const registered = await register(vapidPublicKey);

  if (previousEndpoint && previousEndpoint !== registered.endpoint) {
    await forgetReplaced(previousEndpoint);
  }

  return registered;
}
